// typescript
import React from "react";
import {Lightbulb} from "lucide-react";

import {Localizer} from "@/main/react/Types";
import {Button} from "@/components/ui/button";

type Props = {
    localizer: Localizer;
    /** The corrected address to offer, or null/undefined to render nothing. */
    suggestion: string | null | undefined;
    /** Called with the corrected address when the user accepts. */
    onAccept: (suggestion: string) => void;
    /** Called when the user keeps their own address. */
    onDismiss: () => void;
    /** Optional base id for stable browser-test hooks. */
    playwrightId?: string;
};

/**
 * Gentle, inline "Did you mean …?" nudge shown below an email field when we
 * detect a likely typo. Deliberately calm: it lives in the same slot as a field
 * error but is styled as a helpful hint (never destructive) and announced
 * politely, so a real validation error always outranks it.
 *
 * Design: designs/04-live/2026-06-10-email-typo-nudge-ux-design.md
 */
export function EmailTypoSuggestion({localizer, suggestion, onAccept, onDismiss, playwrightId}: Props) {
    if (!suggestion) {
        return null;
    }

    // Split the localized template on its {0} placeholder so we can emphasize the
    // suggested address while keeping the surrounding sentence fully translatable.
    const template = localizer("email-typo.suggestion.prompt", "{0}");
    const [before, after] = template.split("{0}");

    return (
        <div
            role="status"
            aria-live="polite"
            className="mt-0.5 flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-center text-sm text-muted-foreground"
            data-playwright-id={playwrightId}
        >
            <span className="inline-flex items-center gap-1.5">
                <Lightbulb className="size-4 shrink-0 text-primary" aria-hidden="true" />
                <span>
                    {before}
                    <strong className="font-medium text-foreground">{suggestion}</strong>
                    {after ?? ""}
                </span>
            </span>
            <span className="inline-flex items-center gap-1">
                <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    className="min-h-9"
                    onClick={() => onAccept(suggestion)}
                    data-playwright-id={playwrightId ? `${playwrightId}-accept` : undefined}
                >
                    {localizer("email-typo.suggestion.accept")}
                </Button>
                <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    className="min-h-9 text-muted-foreground"
                    onClick={onDismiss}
                    data-playwright-id={playwrightId ? `${playwrightId}-dismiss` : undefined}
                >
                    {localizer("email-typo.suggestion.dismiss")}
                </Button>
            </span>
        </div>
    );
}
